/**
 * What the courier's offer inbox is showing, from what the stream has said, away from any rendering.
 *
 * Three designed screens over one connection — `2 Online`, `3 Offer`, `3b Offer expired` — and the
 * design's own note says the third is reached without anybody pressing anything: _"3: a 15-second
 * countdown ticks live; under 5 s the ring turns amber and pulses. Try it: it expires into 3b on its
 * own."_ So which screen is drawn is a function of the stream and the clock, and that function is
 * here rather than inside a component, because a clock-driven decision that can only be checked by
 * rendering it is a clock-driven decision nobody checks.
 *
 * ## The countdown, which is the thing this file exists to get right
 *
 * R-10 recorded this defect on the dispatch console's offer board: an offer's remaining time was
 * **wrong by one poll interval**, because it was computed where the data was fetched rather than
 * where it was drawn. EXP-003 does not repeat it — `OfferEvent.expiresAt` is documented as "the
 * server instant the offer stops being acceptable" and "never a countdown", for exactly this reason
 * — so the remaining time is computed here, on the phone's own clock, against that instant, every
 * frame it is drawn.
 *
 * **The window is the offer's own, not fifteen seconds.** The design's ring runs `left/15` because
 * its sample offer is a fifteen-second one. DSP-02 takes `expiresAt` as a term from DSP-01 and
 * enforces only that it is after `createdAt`, so the real window is `expiresAt - createdAt` and
 * both ends are published on every event. Hard-coding fifteen would have drawn a ring already
 * three-quarters gone on a sixty-second offer.
 */

import type { OfferEvent } from "@tdelivr/api-client/generated/experience/driver-bff.js";

/** Which of the three designed screens the inbox is on. */
export type InboxScreen = "online" | "offer" | "offer-expired";

/**
 * What `stream-open` reported about the connection, which is more than "it opened".
 *
 * `discovery` is the field worth reading. A courier's own token cannot reach DSP-02's listing — its
 * 403 names that case — so a connection opened with a courier's token can follow the offers it was
 * told about and **cannot find one by itself**. EXP-003 publishes that as `discovery: false` so an
 * application can say so rather than wait for something that will never arrive. R-22, and the
 * design has no words for it: Q-49.
 */
export interface StreamOpen {
  readonly discovery: boolean;
  readonly watching: readonly string[];
  readonly pollIntervalMs: number;
  readonly maxLifetimeMs: number;
  readonly cursorWasUnreadable: boolean;
}

/** Everything the inbox knows, which is the stream's events and nothing else. */
export interface InboxState {
  /** Null until `stream-open` arrives. */
  readonly open: StreamOpen | null;
  /**
   * The offer being shown, if any.
   *
   * One rather than a list: the design draws one offer at a time, and a courier deciding between
   * two offers in ten seconds is a screen nobody designed. The newest non-terminal offer wins, and
   * a terminal one is kept only so screen 3b can say what happened to it.
   */
  readonly offer: OfferEvent | null;
  /** True once the connection has ended and will not be reopened. */
  readonly ended: boolean;
}

export const emptyInbox: InboxState = { open: null, offer: null, ended: false };

/**
 * Fold one `offer` event into the inbox.
 *
 * `version` decides, not arrival order. DSP-02's version is monotonic per offer and EXP-003 admits
 * an event only when its version exceeds the last one sent — but a client that reconnects with a
 * cursor and a client that is handed two connections' worth of events must make the same decision
 * for itself, because the ordering guarantee is per connection and a reconnect is a new one.
 */
export function withOffer(state: InboxState, event: OfferEvent): InboxState {
  const held = state.offer;
  if (held !== null && held.offerId === event.offerId && event.version <= held.version) {
    return state;
  }
  /*
   * A second offer while a live one is on screen does not replace it.
   *
   * The courier is looking at a countdown and pressing a slider; swapping the offer underneath them
   * would mean accepting one they never read. The newer one is dropped rather than queued, because
   * nothing in the design draws a queue and EXP-003 will still be watching it on the next
   * connection.
   */
  if (
    held !== null &&
    held.offerId !== event.offerId &&
    !held.terminal &&
    !isTerminalStatus(held.status)
  ) {
    return state;
  }
  return { ...state, offer: event };
}

/** The four statuses after which an offer will never change again, as DSP-02 defines them. */
export function isTerminalStatus(status: OfferEvent["status"]): boolean {
  return (
    status === "accepted" || status === "rejected" || status === "expired" || status === "cancelled"
  );
}

/**
 * Which screen the inbox draws, from the offer it holds and the instant it is being drawn at.
 *
 * The clock is a parameter rather than a call to `Date.now()`, which is what lets a test put an
 * offer one millisecond either side of its own expiry without waiting.
 *
 * **An offer whose `expiresAt` has passed is screen 3b even while DSP-02 still calls it `created`.**
 * That is deliberate and it is the design's own rule: the note says an accept arriving after the
 * timer "is refused and says so", and DSP-02's route agrees — it refuses any transition other than
 * `expire` once `expiresAt` has passed. A courier shown a slider over an offer that cannot be
 * accepted has been shown a button that will fail.
 */
export function screenFor(state: InboxState, now: number): InboxScreen {
  const offer = state.offer;
  if (offer === null) return "online";
  if (offer.status === "expired" || offer.status === "cancelled") return "offer-expired";
  if (offer.status === "accepted" || offer.status === "rejected") return "online";
  return Date.parse(offer.expiresAt) <= now ? "offer-expired" : "offer";
}

/* ----------------------------------------------------------------------------- the countdown face */

/** What the design's ring and its number need, computed once per frame. */
export interface CountdownFace {
  /** `0:07`, or `1:30` for a window longer than a minute. */
  readonly label: string;
  /** 1 at the start of the window, 0 at its end. The conic gradient's stop. */
  readonly fraction: number;
  /** True under five seconds left, when the design turns the ring amber and beats it. */
  readonly urgent: boolean;
  readonly remainingMs: number;
}

/**
 * Under how many seconds remaining the ring turns amber and pulses.
 *
 * `left<=5` in the design. A product number, so it is named where a product person can find it.
 */
export const URGENT_BELOW_SECONDS = 5;

/**
 * The countdown, from the offer's own two instants and the phone's clock.
 *
 * Three things the design does and this keeps:
 *
 *   - **It never goes below zero.** `Math.max(0,left)` in the design, and here too: a negative
 *     remaining time would make the conic gradient's stop negative and the ring would fill again.
 *   - **It rounds up, not down.** `Math.ceil`, so an offer with 6.4 seconds left reads `0:07` and
 *     reaches `0:00` only when it is actually gone. Rounding down would show `0:00` for a whole
 *     second while the offer was still acceptable, which is a courier not pressing a slider that
 *     would have worked.
 *   - **The label is the design's, generalised by one digit.** The design writes
 *     `` `0:${pad(left)}` `` because its sample window is fifteen seconds. A real window is
 *     whatever DSP-01 set, so this is `M:SS` — identical to the design for every window under a
 *     minute, and correct rather than `0:90` above one. Recorded as an assumption in Q-60.
 */
export function countdownFace(offer: OfferEvent, now: number): CountdownFace {
  const expiresAt = Date.parse(offer.expiresAt);
  const createdAt = Date.parse(offer.createdAt);
  /*
   * `Math.max(0, NaN)` is `NaN`, which is the whole reason this is a named step.
   *
   * An unparseable `expiresAt` would otherwise carry `NaN` into the conic gradient's stop, and a
   * `NaN` in a CSS value drops the declaration — so the ring would not be empty, it would not be
   * drawn at all, and the hero would be blank over a live offer. The floor is applied to the
   * subtraction's finiteness and not only to its sign.
   */
  const until = expiresAt - now;
  const remainingMs = Number.isFinite(until) ? Math.max(0, until) : 0;
  /*
   * A window of at most zero cannot happen through DSP-02 — its domain refuses an `expiresAt` that
   * is not after `createdAt` — but an unparseable instant produces `NaN`, and `Math.max(1, NaN)` is
   * `NaN` rather than 1. That is the trap: `NaN` in a conic gradient drops the whole declaration and
   * draws no ring at all, so the hero would be blank over a live offer rather than empty. Checked
   * for finiteness and not only for sign, the same as `remainingMs` above.
   */
  const span = expiresAt - createdAt;
  const windowMs = Number.isFinite(span) ? Math.max(1, span) : 1;
  const seconds = Math.ceil(remainingMs / 1000);
  return {
    label: `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`,
    fraction: Math.min(1, remainingMs / windowMs),
    urgent: seconds <= URGENT_BELOW_SECONDS,
    remainingMs,
  };
}

/**
 * How often the countdown is redrawn while an offer is on screen.
 *
 * 250ms rather than 1000ms, and the reason is the label's own rounding. At a one-second interval the
 * displayed number can sit a whole tick behind the instant it describes, which is R-10's defect with
 * a smaller constant — the ring and the number would disagree with the slider's own refusal. Four
 * times a second is imperceptible as motion and keeps the two within a quarter-second of each other.
 */
export const COUNTDOWN_TICK_MS = 250;

/* --------------------------------------------------------- what the design draws and nothing has */

/**
 * The drawn elements of screens 2 and 3 that nothing on this platform publishes.
 *
 * Named here, in one place, rather than inlined at each render site, so the list can be read against
 * `tests/contract/mobile-screen-support.test.ts` — which names the same absences from the outside —
 * and so adding a field to a contract has one place to delete from. Each is rendered as the absence,
 * never as a plausible value.
 *
 *   - `todayEarnings` — `Today · 3 jobs · TZS 7,200` on screen 2, and the earnings pill in the app
 *     bar of every screen in both files. Nothing publishes `earnings.totalMinor`,
 *     `summary.earningsMinor` or `earnings.netMinor`; EXP-003 answers `earnings: null` with an
 *     `earningsAbsence`. R-22.
 *   - `onlineSince` — `Online for 1 h 12 min`. DRV-05 publishes `presence`, `availability`,
 *     `reasonCode`, `dispatchable`, `fresh` and `leaseExpiresAt`, and nothing that says when this
 *     online period began.
 *   - `zoneName` — `Masaki zone` in screen 2's sub-line. GEO-06 names an area and its availability
 *     query requires a `productRef` that no driver record publishes, so the name is reachable only
 *     when a caller supplies that product.
 *   - `payout` — `You earn TZS 2,400` on screen 3. Nothing publishes `offer.payoutMinor`,
 *     `offers.payoutMinor` or `offer.earningsMinor`.
 *   - `pickupBy` — `Pickup by 10:30`. `expiresAt` is when the *offer* stops being acceptable, not
 *     when the parcel must be collected, and using one for the other would be the one invention
 *     these rules forbid most plainly.
 *   - `pickupAddress` / `dropoffAddress` — `Pickup 1.2 km · Masaki` and
 *     `Drop-off Oyster Bay · 3.1 km · small parcel`. Nothing publishes
 *     `offer.pickup.addressLabel`, `offers.pickup` or `offer.pickupAddressLabel`; the event carries
 *     MOB-03's `rideRequestId` and the courier's token cannot read that request.
 */
export const OFFER_ABSENCES = [
  "todayEarnings",
  "onlineSince",
  "zoneName",
  "payout",
  "pickupBy",
  "pickupAddress",
  "dropoffAddress",
] as const;

export type OfferAbsence = (typeof OFFER_ABSENCES)[number];
